import { Box } from "@mui/material";
import React, { useState } from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import SecondaryTabMenuPicker from "../../../../common/SecondaryTabMenuPicker";
import {
  formatDifficulty,
  formatPrepTime,
  getMealItemScore,
  getPortionInfo,
} from "../../helpers/mealItemHelpers";
import {
  getMealItemState,
  getStatusTagType,
} from "../../helpers/mealItemStateHelpers";
import { Diet_Types } from "../../model/dietTypes";
import { useNutritionPlan } from "../../store/useNutritionPlan";
import { getStatusBadgeStyle } from "../../../../../utils/labPanelStatus";
import LoggedBadge from "../shared/LoggedBadge";
import MealImage from "../shared/MealImage";
import PlanSidePanel from "../../../shared/PlanSidePanel";
import AdditionalInformation from "./AdditionalInformation";
import DetailsMetadataRow from "./DetailsMetadataRow";
import NutritionFactsSection from "./NutritionFactsSection";
import RecipeIngredientsList from "./RecipeIngredientsList";
import RecipeInstructionsList from "./RecipeInstructionsList";

const RECIPE_TABS = [
  { label: "Recipe", key: 0 },
  { label: "Ingredients", key: 1 },
];

const HERO_HEIGHT = 220;

const STATUS_LABELS = {
  skipped: "Skipped",
  swapped: "Swapped",
  extras: "Extra",
  personalized: "Personalized",
} as const;

/**
 * Food / recipe details in the right drawer — the mobile
 * `FoodMealItemDetailsScreen` and `RecipeMealItemDetailsScreen` without the
 * action bar (like, swap, edit arrive with the mutations in Phase 2).
 */
const MealItemDetailsPanel = () => {
  const theme: Theme = useTheme();
  const { details, closeDetails } = useNutritionPlan();
  const [recipeTab, setRecipeTab] = useState(0);

  const resolved = details?.resolved;
  const mealItem = resolved?.mealItem;
  const state = mealItem ? getMealItemState(mealItem) : null;
  const statusTag =
    mealItem && resolved
      ? getStatusTagType(mealItem, resolved.isExtra, resolved.isAlternative)
      : null;
  const score = mealItem ? getMealItemScore(mealItem) : null;
  const dietNames = (mealItem?.food ?? mealItem?.recipe)?.diet_types
    ?.map((dietType) => Diet_Types[dietType.diet_type]?.name)
    .filter((name): name is string => !!name);

  return (
    <PlanSidePanel
      open={!!details}
      onClose={closeDetails}
      title={mealItem?.food ? "Food details" : "Recipe details"}
    >
      {mealItem && state && resolved && (
        <>
          <Box sx={styles.hero}>
            <MealImage
              src={state.photoUrl}
              alt={state.title}
              height={HERO_HEIGHT}
            />
          </Box>

          <Box sx={styles.titleBlock}>
            <Box sx={styles.title(theme)}>{state.title}</Box>
            {dietNames && dietNames.length > 0 && (
              <Box sx={styles.subtitle(theme)}>{dietNames.join(" · ")}</Box>
            )}
            {/* Status pills under the title, where the mobile details screen
                puts them: Swapped / Skipped / Extra / Personalized, then
                Nutritionist approved, then Logged. */}
            {(statusTag || resolved.isNutritionistApproved || state.isLogged) && (
              <Box sx={styles.pillsRow}>
                {statusTag && (
                  <Box sx={getStatusBadgeStyle("warning", theme)}>
                    {STATUS_LABELS[statusTag]}
                  </Box>
                )}
                {resolved.isNutritionistApproved && (
                  <Box sx={getStatusBadgeStyle("info", theme)}>
                    Nutritionist approved
                  </Box>
                )}
                {state.isLogged && <LoggedBadge />}
              </Box>
            )}
          </Box>

          {mealItem.food ? (
            <DetailsMetadataRow
              leading={{
                header: "Serving size",
                value: getPortionInfo(mealItem.food),
              }}
              score={score}
            />
          ) : (
            <DetailsMetadataRow
              leading={{
                header: "Preparation",
                value: formatPrepTime(mealItem.recipe?.prepare_duration),
              }}
              score={score}
              trailing={{
                header: "Difficulty",
                value: formatDifficulty(mealItem.recipe?.difficulty_level),
              }}
            />
          )}

          <NutritionFactsSection mealItem={mealItem} />

          {mealItem.recipe && (
            <Box sx={styles.recipeSection}>
              <SecondaryTabMenuPicker
                tabs={RECIPE_TABS}
                selectedTab={recipeTab}
                setSelectedTab={setRecipeTab}
                isFullWidth={false}
              />
              {recipeTab === 0 ? (
                <RecipeInstructionsList
                  instructionsText={mealItem.recipe.instructions_text}
                />
              ) : (
                <RecipeIngredientsList
                  ingredients={mealItem.recipe.items ?? []}
                />
              )}
            </Box>
          )}

          {mealItem.food ? (
            <AdditionalInformation kind="food" item={mealItem.food} />
          ) : mealItem.recipe ? (
            <AdditionalInformation kind="recipe" item={mealItem.recipe} />
          ) : null}
        </>
      )}
    </PlanSidePanel>
  );
};

export default MealItemDetailsPanel;

const styles = {
  hero: {
    position: "relative" as "relative",
    borderRadius: "16px",
    overflow: "hidden",
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  pillsRow: {
    display: "flex",
    flexWrap: "wrap" as "wrap",
    gap: "8px",
    paddingTop: "8px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  subtitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  recipeSection: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
};
